import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Users, Mail, Copy, Check,
  Crown, Star, Briefcase, User, Eye,
  ClipboardList, Trophy, ChevronRight,
  Target, Award,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { orgService } from '@core/supabase/services/orgService';
import { applicationService } from '@core/supabase/services/applicationService';
import { OrgUser, OrgRole, Application } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { StatusBadge } from '@shared/components/ui/StatusBadge';

const ROLE_ORDER: OrgRole[] = ['owner', 'admin', 'senior_recruiter', 'junior_recruiter', 'viewer'];

const ROLE_META: Record<OrgRole, { label: string; short: string; icon: typeof Crown }> = {
  owner: { label: 'Owner', short: 'Owner', icon: Crown },
  admin: { label: 'Admin', short: 'Admin', icon: Star },
  senior_recruiter: { label: 'Senior Recruiter', short: 'Senior', icon: Briefcase },
  junior_recruiter: { label: 'Junior Recruiter', short: 'Junior', icon: User },
  viewer: { label: 'Viewer', short: 'Viewer', icon: Eye },
};

interface MemberWithStats extends OrgUser {
  stats: { applications: number; placed: number; candidates: number };
}

interface Connection { from: string; to: string }

/**
 * Clickable node with ref for network lines.
 *
 * C-47: each node is a <Link to="/team/:id"> so the primary click
 * navigates to the RBAC-gated profile page. Hover reveals a quick-
 * stats card (Phase 8 §34.1). The old right-rail "select" behaviour
 * stays reachable via /team/member/:id for legacy deep links.
 */
const ROLE_COLORS: Record<OrgRole, { bg: string; border: string; text: string }> = {
  owner: { bg: 'rgba(251, 191, 36, 0.15)', border: 'rgba(251, 191, 36, 0.4)', text: '#fbbf24' },
  admin: { bg: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.4)', text: '#a78bfa' },
  senior_recruiter: { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.4)', text: '#10b981' },
  junior_recruiter: { bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.4)', text: '#3b82f6' },
  viewer: { bg: 'rgba(107, 114, 128, 0.15)', border: 'rgba(107, 114, 128, 0.4)', text: '#6b7280' },
};

function NetworkNode({
  member,
  isSelected,
  isYou,
  nodeRef,
}: {
  member: MemberWithStats;
  isSelected: boolean;
  isYou: boolean;
  nodeRef: (id: string, el: HTMLAnchorElement | null) => void;
}) {
  const meta = ROLE_META[member.role];
  const colors = ROLE_COLORS[member.role];
  const Icon = meta.icon;
  const initials = (member.display_name ?? member.email).split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <div className="group relative z-10">
      <Link
        ref={(el) => nodeRef(member.id, el)}
        to={`/team/${member.id}`}
        data-node-id={member.id}
        data-role={member.role}
        className={`
          team-org-node flex min-w-[100px] max-w-[130px] cursor-pointer flex-col items-center justify-center rounded-xl p-3 transition-all
          ${isSelected ? 'scale-105 shadow-lg ring-2 ring-brand-500/60 ring-offset-2 ring-offset-[var(--vscode-bg-primary)]' : 'hover:scale-[1.02] hover:shadow-md'}
        `}
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
        }}
      >
        <div
          className="mb-1 flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold"
          style={{
            background: colors.bg,
            border: `2px solid ${colors.border}`,
            color: colors.text,
          }}
        >
          {initials}
        </div>
        <p className="w-full truncate text-center text-xs font-semibold vscode-text-primary">
          {member.display_name ?? member.email.split('@')[0]}
          {isYou && <span className="vscode-text-tertiary"> (you)</span>}
        </p>
        <span
          className="mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
          style={{ color: colors.text }}
        >
          <Icon className="h-2.5 w-2.5" /> {meta.short}
        </span>
      </Link>

      {/* Hover card — 4 quick stats, keyboard users get the same info via the aria-label on the link target. */}
      <div
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 hidden -translate-x-1/2 rounded-xl border vscode-border vscode-bg-elevated p-4 shadow-xl group-hover:block"
        style={{ width: 220 }}
      >
        <div className="flex items-center gap-2 mb-2">
          <div
            className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ background: colors.bg, border: `2px solid ${colors.border}`, color: colors.text }}
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold vscode-text-primary">
              {member.display_name ?? member.email}
            </p>
            <p className="text-[10px] font-medium" style={{ color: colors.text }}>
              {meta.label}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <Stat label="Assigned" value={member.stats.applications} color="blue" />
          <Stat label="Placed" value={member.stats.placed} color="green" />
          <Stat label="Candidates" value={member.stats.candidates} color="purple" />
          <Stat label="Role" value={meta.short} color="gray" />
        </div>
        <p className="mt-3 text-[10px] vscode-text-tertiary flex items-center gap-1">
          <ChevronRight className="h-3 w-3" /> Click to open full profile
        </p>
      </div>
    </div>
  );
}

const STAT_COLORS: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'rgba(59, 130, 246, 0.12)', text: '#3b82f6' },
  green: { bg: 'rgba(16, 185, 129, 0.12)', text: '#10b981' },
  purple: { bg: 'rgba(139, 92, 246, 0.12)', text: '#a78bfa' },
  amber: { bg: 'rgba(245, 158, 11, 0.12)', text: '#f59e0b' },
  gray: { bg: 'rgba(107, 114, 128, 0.12)', text: '#9ca3af' },
};

function Stat({ label, value, color = 'gray' }: { label: string; value: number | string; color?: keyof typeof STAT_COLORS }) {
  const colors = STAT_COLORS[color];
  return (
    <div
      className="rounded-lg px-2.5 py-1.5"
      style={{ background: colors.bg }}
    >
      <p className="text-[9px] uppercase tracking-wider vscode-text-tertiary">{label}</p>
      <p className="text-[13px] font-bold" style={{ color: colors.text }}>{value}</p>
    </div>
  );
}

export function TeamPage() {
  const { id: memberId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = usePermissions();
  const [members, setMembers] = useState<MemberWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(memberId ?? null);
  const [deactivating, setDeactivating] = useState<MemberWithStats | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ full_name: '', email: '', role: 'junior_recruiter' as OrgRole });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [assignedApplications, setAssignedApplications] = useState<Application[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<string, HTMLAnchorElement>>({});
  const [paths, setPaths] = useState<string[]>([]);

  useEffect(() => {
    if (memberId) setSelectedId(memberId);
  }, [memberId]);

  useEffect(() => {
    if (!user?.orgId || !selectedId) {
      setAssignedApplications([]);
      return;
    }
    applicationService.list(user.orgId, { assignedTo: selectedId }).then((list) =>
      setAssignedApplications(Array.isArray(list) ? list.slice(0, 10) : [])
    );
  }, [user?.orgId, selectedId]);

  useEffect(() => {
    if (!user?.orgId) return;
    Promise.all([
      orgService.getTeamMembers(user.orgId),
      orgService.getTeamStats(user.orgId),
    ]).then(([teamMembers, stats]) => {
      setMembers(
        teamMembers.map((m) => ({
          ...m,
          stats: {
            applications: stats.byUserId[m.id]?.applications ?? 0,
            placed: stats.byUserId[m.id]?.placed ?? 0,
            candidates: stats.byKeycloakSub[m.keycloak_sub]?.candidates ?? 0,
          },
        })),
      );
    }).finally(() => setLoading(false));
  }, [user?.orgId]);

  const nodeRef = (id: string, el: HTMLAnchorElement | null) => {
    if (el) nodeRefs.current[id] = el;
    else delete nodeRefs.current[id];
  };

  useLayoutEffect(() => {
    if (members.length === 0) return;
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const connections: Connection[] = [];
    const byRole = ROLE_ORDER.reduce((acc, r) => {
      acc[r] = members.filter((m) => m.role === r);
      return acc;
    }, {} as Record<OrgRole, MemberWithStats[]>);

    // Define hierarchy: Owner → Admin+Senior; Admin → Viewer; Senior → Junior
    byRole.admin.forEach((a) => byRole.owner.forEach((o) => connections.push({ from: o.id, to: a.id })));
    byRole.senior_recruiter.forEach((s) => byRole.owner.forEach((o) => connections.push({ from: o.id, to: s.id })));
    byRole.viewer.forEach((v) => byRole.admin.forEach((a) => connections.push({ from: a.id, to: v.id })));
    byRole.junior_recruiter.forEach((j) => byRole.senior_recruiter.forEach((s) => connections.push({ from: s.id, to: j.id })));

    const svgPaths: string[] = [];
    connections.forEach(({ from, to }) => {
      const fromEl = nodeRefs.current[from];
      const toEl = nodeRefs.current[to];
      if (!fromEl || !toEl || !container) return;
      const fromR = fromEl.getBoundingClientRect();
      const toR = toEl.getBoundingClientRect();
      const cx = (r: DOMRect) => r.left - rect.left + r.width / 2;
      const cy = (r: DOMRect) => r.top - rect.top + r.height;
      const x1 = cx(fromR);
      const y1 = cy(fromR);
      const x2 = cx(toR);
      const y2 = toR.top - rect.top;
      const midY = (y1 + y2) / 2;
      svgPaths.push(`M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`);
    });
    setPaths(svgPaths);
  }, [members]);

  const handleDeactivate = async (member: MemberWithStats) => {
    if (!user?.orgId) return;
    await orgService.deactivateMember(user.orgId, member.keycloak_sub);
    setMembers((prev) => prev.filter((m) => m.keycloak_sub !== member.keycloak_sub));
    setDeactivating(null);
    if (selectedId === member.id) setSelectedId(null);
  };

  const handleAddMember = async () => {
    if (!user?.orgId || !inviteForm.full_name.trim() || !inviteForm.email.trim()) return;
    setInviteSaving(true);
    setInviteError(null);
    try {
      const newMember = await orgService.addMember(user.orgId, {
        full_name: inviteForm.full_name.trim(),
        email: inviteForm.email.trim(),
        role: inviteForm.role,
      });
      setMembers((prev) => [...prev, { ...newMember, stats: { applications: 0, placed: 0, candidates: 0 } }]);
      setInviteForm({ full_name: '', email: '', role: 'junior_recruiter' });
      setShowInviteForm(false);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setInviteSaving(false);
    }
  };

  const inviteLink = `${window.location.origin}/onboarding?orgId=${user?.orgId}`;
  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const byRole = ROLE_ORDER.reduce((acc, r) => {
    acc[r] = members.filter((m) => m.role === r);
    return acc;
  }, {} as Record<OrgRole, MemberWithStats[]>);

  const selected = selectedId ? members.find((m) => m.id === selectedId) : null;

  // Legacy: `/team/member/:id` URL preselects a member for the right rail.
  // The primary click-through is now the C-47 profile page (NetworkNode →
  // Link to `/team/:id`). `navigate` is retained for any imperative flow.
  void navigate;

  return (
    <div>
      <PageHeader
        breadcrumb={[{ label: 'Organization', href: '/settings/organisation' }, { label: 'Team' }]}
        title="Team"
        subtitle="Org hierarchy. Click any member to view profile and assignments."
        breadcrumbRowEnd={
          <ListOperationsDropdown
            objectHistoryHref={objectHistoryUrl('team')}
            onExport={(fmt, scope) => listExportNotImplemented('Team', fmt, scope)}
          />
        }
        action={
          perms.canManageTeam && (
            <div className="flex items-center gap-2">
              <button onClick={copyInviteLink} className="btn-secondary flex items-center gap-1.5 text-sm">
                {inviteCopied ? <><Check className="w-3.5 h-3.5 text-green-400" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> [Copy link]</>}
              </button>
              <button onClick={() => setShowInviteForm((v) => !v)} className="btn-primary flex items-center gap-1.5 text-sm">
                <Mail className="w-3.5 h-3.5" /> [Add member]
              </button>
            </div>
          )
        }
      />

      {perms.canManageTeam && !showInviteForm && (
        <div className="mb-5 rounded-xl border vscode-border vscode-bg-tertiary/60 p-4 text-sm vscode-text-secondary">
          <p className="mb-1 font-medium vscode-text-primary">Keycloak: GoGrabJob Business</p>
          <p>Add users in Keycloak and sync, or use <strong>Add member</strong> to create a pending invite row.</p>
        </div>
      )}

      {/* Inline add-member form */}
      {perms.canManageTeam && showInviteForm && (
        <div className="mb-5 rounded-xl border vscode-border vscode-bg-secondary p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-semibold vscode-text-primary">[Add team member]</p>
            <button onClick={() => { setShowInviteForm(false); setInviteError(null); }} className="text-xs vscode-text-tertiary hover:vscode-text-primary">Cancel</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs vscode-text-tertiary mb-1 block">Full name</label>
              <input
                className="input w-full"
                placeholder="e.g. Priya Sharma"
                value={inviteForm.full_name}
                onChange={(e) => setInviteForm((f) => ({ ...f, full_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs vscode-text-tertiary mb-1 block">Work email</label>
              <input
                className="input w-full"
                type="email"
                placeholder="priya@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs vscode-text-tertiary mb-1 block">Role</label>
              <select
                className="input w-full"
                value={inviteForm.role}
                onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value as OrgRole }))}
              >
                {ROLE_ORDER.filter((r) => r !== 'owner').map((r) => (
                  <option key={r} value={r}>{ROLE_META[r].label}</option>
                ))}
              </select>
            </div>
          </div>
          {inviteError && <p className="text-xs text-red-400">{inviteError}</p>}
          <div className="flex justify-end">
            <button
              className="btn-primary text-sm"
              disabled={inviteSaving || !inviteForm.full_name.trim() || !inviteForm.email.trim()}
              onClick={handleAddMember}
            >
              {inviteSaving ? 'Adding…' : 'Add member'}
            </button>
          </div>
          <p className="text-xs vscode-text-tertiary">
            This creates a pending member row. The user will need a Keycloak account with a matching email to log in.
          </p>
        </div>
      )}

      {loading ? (
        <FullPageSpinner />
      ) : members.length === 0 ? (
        <EmptyState icon={Users} title="No team members yet" description="Add users in Keycloak and sync to link them to your org." />
      ) : (
        <>
          {/* Team summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/20">
                <Users className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-[10px] vscode-text-tertiary uppercase">Total members</p>
                <p className="text-2xl font-bold tabular-nums">{members.length}</p>
              </div>
            </div>
            <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-500/20">
                <Target className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-[10px] vscode-text-tertiary uppercase">Total assigned</p>
                <p className="text-2xl font-bold tabular-nums">{members.reduce((s, m) => s + m.stats.applications, 0)}</p>
              </div>
            </div>
            <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/20">
                <Award className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-[10px] vscode-text-tertiary uppercase">Total placed</p>
                <p className="text-2xl font-bold tabular-nums text-emerald-400">{members.reduce((s, m) => s + m.stats.placed, 0)}</p>
              </div>
            </div>
            <div className="rounded-xl vscode-bg-secondary p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/20">
                <Briefcase className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-[10px] vscode-text-tertiary uppercase">Recruiters</p>
                <p className="text-2xl font-bold tabular-nums">{byRole.senior_recruiter.length + byRole.junior_recruiter.length}</p>
              </div>
            </div>
          </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 lg:gap-8">
          {/* ── Network with connecting lines ─────────────────────────────── */}
          <div className="card overflow-x-auto overflow-y-auto min-h-[400px] min-w-0">
            <div ref={containerRef} className="relative py-10 px-6 min-w-[520px]">
              {/* SVG network lines */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ top: 0, left: 0 }}
              >
                <defs>
                  <linearGradient id="teamLineGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="var(--team-chart-line)" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="var(--team-chart-line)" stopOpacity="0.4" />
                  </linearGradient>
                </defs>
                {paths.map((d, i) => (
                  <path
                    key={i}
                    d={d}
                    fill="none"
                    stroke="url(#teamLineGrad)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </svg>

              {/* Row 1: Owner */}
              {byRole.owner.length > 0 && (
                <div className="flex justify-center mb-8">
                  <div className="flex flex-wrap justify-center gap-4">
                    {byRole.owner.map((m) => (
                      <NetworkNode key={m.id} member={m} isSelected={selectedId === m.id} isYou={m.keycloak_sub === user?.id} nodeRef={nodeRef} />
                    ))}
                  </div>
                </div>
              )}

              {/* Row 2: Admin + Senior */}
              {(byRole.admin.length > 0 || byRole.senior_recruiter.length > 0) && (
                <div className="flex justify-center mb-8">
                  <div className="flex flex-wrap justify-center gap-4">
                    {byRole.admin.map((m) => (
                      <NetworkNode key={m.id} member={m} isSelected={selectedId === m.id} isYou={m.keycloak_sub === user?.id} nodeRef={nodeRef} />
                    ))}
                    {byRole.senior_recruiter.map((m) => (
                      <NetworkNode key={m.id} member={m} isSelected={selectedId === m.id} isYou={m.keycloak_sub === user?.id} nodeRef={nodeRef} />
                    ))}
                  </div>
                </div>
              )}

              {/* Row 3: Junior */}
              {byRole.junior_recruiter.length > 0 && (
                <div className="flex justify-center mb-8">
                  <div className="flex flex-wrap justify-center gap-4">
                    {byRole.junior_recruiter.map((m) => (
                      <NetworkNode key={m.id} member={m} isSelected={selectedId === m.id} isYou={m.keycloak_sub === user?.id} nodeRef={nodeRef} />
                    ))}
                  </div>
                </div>
              )}

              {/* Row 4: Viewer */}
              {byRole.viewer.length > 0 && (
                <div className="flex justify-center">
                  <div className="flex flex-wrap justify-center gap-4">
                    {byRole.viewer.map((m) => (
                      <NetworkNode key={m.id} member={m} isSelected={selectedId === m.id} isYou={m.keycloak_sub === user?.id} nodeRef={nodeRef} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Right sidebar: selected member profile & assignments ───────── */}
          <aside className="space-y-6">
            {selected ? (
              <>
                <div className="rounded-lg border vscode-border vscode-bg-elevated p-4">
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">[Profile]</h3>
                  <div className="mb-4 flex items-center gap-3">
                    <div
                      data-role={selected.role}
                      className="team-org-node flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold vscode-text-primary"
                    >
                      {(selected.display_name ?? selected.email).split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold vscode-text-primary">{selected.display_name ?? '—'}</p>
                      <p className="text-xs vscode-text-tertiary">{ROLE_META[selected.role].label}</p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs vscode-text-tertiary">
                        <Mail className="h-3 w-3" /> {selected.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <div className="flex items-center gap-1.5">
                      <ClipboardList className="h-3.5 w-3.5 vscode-text-tertiary" />
                      <span className="vscode-text-secondary">Assigned:</span>
                      <span className="font-semibold vscode-text-primary">{selected.stats.applications}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 vscode-text-tertiary" />
                      <span className="vscode-text-secondary">Candidates:</span>
                      <span className="font-semibold vscode-text-primary">{selected.stats.candidates}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Trophy className="h-3.5 w-3.5 text-[var(--ggj-success,#22c55e)]" />
                      <span className="vscode-text-secondary">Placed:</span>
                      <span className="font-semibold text-[var(--ggj-success,#22c55e)]">{selected.stats.placed}</span>
                    </div>
                  </div>
                  {perms.canManageTeam && selected.keycloak_sub !== user?.id && selected.role !== 'owner' && (
                    <div className="mt-3 flex gap-2 border-t vscode-border pt-3">
                      <button className="btn-secondary text-xs py-1.5 px-2" disabled>[Change role]</button>
                      <button onClick={() => setDeactivating(selected)} className="btn-secondary text-xs py-1.5 px-2 text-red-400 hover:text-red-300 hover:border-red-600">Deactivate</button>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border vscode-border vscode-bg-elevated p-4">
                  <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">
                    <ClipboardList className="h-3.5 w-3.5" /> [Assigned Applications]
                  </h3>
                  {assignedApplications.length === 0 ? (
                    <p className="text-xs vscode-text-tertiary">No applications assigned.</p>
                  ) : (
                    <ul className="space-y-2">
                      {assignedApplications.map((app) => (
                        <li key={app.id} className="border-b vscode-border pb-2 last:border-0 last:pb-0">
                          <Link to={`/applications/${app.id}`} className="group block">
                            <span className="block truncate text-sm font-medium vscode-text-primary group-hover:text-[var(--vscode-text-link)]">
                              {app.candidate?.full_name ?? '—'} → {app.job?.title ?? '—'}
                            </span>
                            <div className="mt-0.5 flex items-center justify-between gap-2">
                              <StatusBadge status={app.status} />
                              <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 vscode-text-tertiary group-hover:text-[var(--vscode-text-link)]" />
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {selected.stats.applications > assignedApplications.length && (
                    <Link
                      to={`/applications?assignedTo=${selected.id}`}
                      className="mt-2 block text-xs vscode-text-link hover:opacity-90"
                    >
                      View all assigned applications →
                    </Link>
                  )}
                </div>
              </>
            ) : (
              <div className="rounded-lg border vscode-border vscode-bg-elevated p-4 text-center">
                <p className="text-sm vscode-text-tertiary">Click a team member to view their profile and assignments.</p>
              </div>
            )}

            {/* Role legend */}
            <div className="flex flex-wrap gap-3 text-xs vscode-text-secondary">
              {ROLE_ORDER.map((role) => {
                const meta = ROLE_META[role];
                const Icon = meta.icon;
                const count = byRole[role].length;
                return (
                  <span key={role} className="flex items-center gap-1.5">
                    <span
                      data-role={role}
                      className="team-org-node inline-flex h-2.5 w-2.5 shrink-0 rounded-full border-2 p-0"
                      aria-hidden
                    />
                    <Icon className="h-3.5 w-3.5 vscode-text-tertiary" />
                    {meta.label} ({count})
                  </span>
                );
              })}
            </div>
          </aside>
        </div>
        </>
      )}

      <ConfirmDialog
        open={!!deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={() => deactivating && handleDeactivate(deactivating)}
        title="Remove team member"
        subtitle="Immediate loss of organisation access"
        message={`Remove ${deactivating?.display_name ?? deactivating?.email ?? 'this member'}? They will lose access immediately.`}
        confirmLabel="Remove"
        requireTypedConfirm={deactivating?.display_name ?? deactivating?.email ?? 'DELETE'}
      />
    </div>
  );
}
